import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Filter, 
  User, 
  Users, 
  Calendar, 
  Globe,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminConsentLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'client', 'partner'
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 20;

  const adminToken = localStorage.getItem('adminToken');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${adminToken}` }
  };

  useEffect(() => {
    fetchLogs();
  }, [filter, page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: (page * limit).toString(),
        limit: limit.toString()
      });
      
      if (filter !== 'all') {
        params.append('user_type', filter);
      }

      const response = await axios.get(
        `${API}/admin/consent-logs?${params}`,
        axiosConfig
      );
      
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch consent logs:', error);
      toast.error('Failed to fetch consent logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    // Convert to IST
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }) + ' IST';
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 space-y-6" data-testid="admin-consent-logs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-amber-400" />
            Consent Logs
          </h1>
          <p className="text-slate-400 mt-1">
            View all user consent records with timestamps and IP addresses
          </p>
        </div>
        <Button
          onClick={fetchLogs}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-400 mr-2">Filter by:</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => { setFilter('all'); setPage(0); }}
                className={filter === 'all' 
                  ? 'bg-amber-500 text-slate-900 hover:bg-amber-400' 
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}
              >
                <Users className="w-4 h-4 mr-1" />
                All ({total})
              </Button>
              <Button
                size="sm"
                onClick={() => { setFilter('client'); setPage(0); }}
                className={filter === 'client' 
                  ? 'bg-amber-500 text-slate-900 hover:bg-amber-400' 
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}
              >
                <User className="w-4 h-4 mr-1" />
                Clients
              </Button>
              <Button
                size="sm"
                onClick={() => { setFilter('partner'); setPage(0); }}
                className={filter === 'partner' 
                  ? 'bg-amber-500 text-slate-900 hover:bg-amber-400' 
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}
              >
                <User className="w-4 h-4 mr-1" />
                Partners
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white text-lg">
            Consent Records
            <span className="text-slate-400 text-sm font-normal ml-2">
              ({total} total)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-400">Loading logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No consent logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Date & Time (IST)
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        IP Address
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      User ID
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {logs.map((log, index) => (
                    <tr key={index} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white font-medium">{log.user_name}</p>
                          <p className="text-slate-400 text-sm">{log.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          log.user_type === 'partner' 
                            ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' 
                            : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        }`}>
                          {log.user_type === 'partner' ? 'Partner' : 'Client'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300 text-sm">
                        {formatDate(log.consent_date)}
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-amber-400 text-sm bg-slate-800 px-2 py-1 rounded">
                          {log.ip_address}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-slate-500 text-xs">
                          {log.user_id?.substring(0, 8)}...
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
              <p className="text-sm text-slate-400">
                Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminConsentLogs;
